/**
 * CashForecastPrint — impression du rapport Planning « Cash flows forecast ».
 *
 * Réutilise EXACTEMENT le même calcul que l'écran Cash Outlook
 * (engine/cashModel + engine/cashSeries) → les chiffres du PDF sont
 * rigoureusement identiques à ceux affichés dans la page Trésorerie.
 *
 * Scénario imprimé : celui dont `start_year === year` (sélecteur d'année
 * global), sinon le premier scénario du tenant.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import { loadCashForecastData, computeForecastSeries } from './engine/cashForecastLoader';
import { formatSwissAmount } from './billing/formatSwiss';
import PrintReportHeader from '../hr/print/PrintReportHeader';
import '../hr/print/PeoplePrint.css';

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const toK = (v) => Math.round(num(v) / 1000);
// Cellule en kCHF, signe selon la valeur (— si zéro) — même convention que l'écran.
const cell = (v) => {
  const k = toK(v);
  if (k === 0) return '—';
  return `${k < 0 ? '-' : ''}${formatSwissAmount(Math.abs(k))}`;
};

// Codes couleurs identiques à SynthesisTab.css (var --color-*).
const C = { pos: '#5C7A3E', neg: '#BA5D4A', amber: '#B8862A', navy: '#2B203E' };
const toneColor = (tone, v) => {
  if (toK(v) === 0) return undefined;            // zéro affiché en « — » neutre
  if (tone === 'pos') return C.pos;
  if (tone === 'neg') return C.neg;
  if (tone === 'amber') return C.amber;
  if (tone === 'navy') return C.navy;
  if (tone === 'balance') return toK(v) < 0 ? C.neg : C.navy;
  if (tone === 'auto') return toK(v) < 0 ? C.neg : C.pos;
  return undefined;
};

export default function CashForecastPrint({ activeTenant, year, editedAt, onDataReady }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!tenantId) return undefined;
    const done = () => { if (onDataReady) onDataReady('cash-flows-forecast'); };

    (async () => {
      try {
        const loaded = await loadCashForecastData(tenantId, year, activeTenant);
        if (alive) setData(loaded);
      } catch {
        if (alive) setData({ scenario: null });
      }
      done();
    })();
    return () => { alive = false; };
  }, [tenantId, year]); // eslint-disable-line

  const series = useMemo(() => computeForecastSeries(data), [data]);

  if (!data) return <div className="ppl-print__section"><p>{t('common:status.loading')}</p></div>;
  if (!data.scenario) {
    return (
      <section className="ppl-print__section">
        <PrintReportHeader category="Planning" title="Cash flows forecast" year={year} tenant={activeTenant} editedAt={editedAt} />
        <p>{p('Aucun scénario de trésorerie pour cette année.')}</p>
      </section>
    );
  }
  if (!series) return <div className="ppl-print__section"><p>{t('common:status.loading')}</p></div>;

  const s = series.s;
  const calc = series.calc;
  const colOf = (key) => calc.soldes.map(row => num(row[key]));

  // Construction des lignes (valeurs déjà signées : décaissements négatifs).
  const openingVals = s.effectiveSoldeMonthly.map((_, i) => (i === 0 ? s.soldeInit : s.effectiveSoldeMonthly[i - 1]));
  const neg = (arr) => arr.map(v => -num(v));

  const rows = [];
  rows.push({ label: 'Opening cash balance', vals: openingVals, total: s.soldeInit, strong: true, tone: 'balance' });
  rows.push({ label: 'Revenue collected (excl. VAT)', vals: colOf('encHT'), total: s.totalEncHT, tone: 'pos' });
  rows.push({ label: 'VAT collected', vals: colOf('encTVA'), total: s.totalEncTVA, italic: true, tone: 'amber' });
  if (s.hasOpeningRec) rows.push({ label: 'Open receivables collected', vals: colOf('encOpening'), total: s.totalEncOpening, tone: 'pos' });
  rows.push({ label: 'Total inflows (incl. VAT)', vals: colOf('enc'), total: s.totalEnc, strong: true, tone: 'pos' });

  rows.push({ label: p('Charges employeurs'), vals: neg(s.effectiveDecPers), total: -s.effectiveTotalDecPers, strong: true, tone: 'neg' });
  rows.push({ label: t('phrases:p_b03c04eee5'), vals: neg(s.salairesNets), total: -s.persSal, indent: true, tone: 'neg' });
  rows.push({ label: t('phrases:p_61bfef5eb5'), vals: neg(s.csTotalMonthly), total: -s.persCs, indent: true, tone: 'neg' });
  rows.push({ label: p('Assurances (LPP / LAA / IJM)'), vals: neg(s.assurances), total: -s.persAss, indent: true, tone: 'neg' });
  if (Math.abs(s.persOther) > 0.5) {
    const otherVals = calc.soldes.map((row, i) => -(num(row.decPers) - num(s.salairesNets[i]) - num(s.csTotalMonthly[i]) - num(s.assurances[i])));
    rows.push({ label: p('Autres'), vals: otherVals, total: -s.persOther, indent: true, italic: true, tone: 'neg' });
  }
  if (Math.abs(s.persExtras) > 0.5) rows.push({ label: t('phrases:p_c018e36570'), vals: neg(s.extrasMonthly), total: -s.persExtras, tone: 'neg' });
  if (Math.abs(s.persExpenses) > 0.5) rows.push({ label: p('Frais du personnel'), vals: neg(s.expensesMonthly), total: -s.persExpenses, tone: 'neg' });

  rows.push({ label: 'Variable purchases & costs', vals: neg(colOf('decVariablesHT')), total: -s.totalDecVariablesHT, tone: 'neg' });
  s.variableDetails.forEach(v => rows.push({ label: v.label, vals: neg(v.monthly), total: -v.monthly.reduce((a, b) => a + b, 0), indent: true, tone: 'neg' }));
  rows.push({ label: 'Recurring overhead', vals: neg(colOf('decMensuelHT')), total: -s.totalDecMensuelHT, tone: 'neg' });
  rows.push({ label: 'One-off overhead', vals: neg(colOf('decSpecifiqueHT')), total: -s.totalDecSpecifiqueHT, tone: 'neg' });
  rows.push({ label: 'Input VAT paid to suppliers', vals: neg(colOf('decExpTVA')), total: -s.totalDecExpTVA, italic: true, tone: 'neg' });
  rows.push({ label: 'VAT remitted to FTA (net)', vals: neg(colOf('decTva')), total: -s.totalDecTva, italic: true, tone: 'neg' });
  if (s.hasOpeningPay) rows.push({ label: 'Open payables settled', vals: neg(colOf('decOpening')), total: -s.totalDecOpening, tone: 'neg' });
  rows.push({ label: 'Operating cash outflow', vals: neg(s.decExploitMonthly), total: -s.totalDecExploit, strong: true, tone: 'neg' });
  rows.push({ label: 'Total outflows', vals: s.decExploitMonthly.map((v, i) => -(num(v) + num(calc.soldes[i].decTva))), total: -s.totalDec, strong: true, tone: 'neg' });

  rows.push({ label: 'Net cash flow', vals: s.effectiveFluxNetMonthly, total: s.totalFluxNet, strong: true, tone: 'auto' });
  rows.push({ label: 'Closing cash balance', vals: s.effectiveSoldeMonthly, total: s.soldeFinal, strong: true, tone: 'balance' });

  return (
    <section className="ppl-print__section ppl-print__section--wide">
      <PrintReportHeader category="Planning" title={`Cash flows forecast — ${data.scenario.name}`} year={year} tenant={activeTenant} editedAt={editedAt} />
      <table className="ppl-print__table">
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Cash flow plan · kCHF</th>
            {s.chartData.map((d, i) => <th key={i}>{d.monthShort} {d.year}</th>)}
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const w = (x) => (r.strong ? <strong>{x}</strong> : x);
            const labelStyle = { textAlign: 'left', paddingLeft: r.indent ? 18 : undefined, fontStyle: r.italic ? 'italic' : undefined };
            return (
              <tr key={i}>
                <td style={labelStyle}>{w(r.label)}</td>
                {r.vals.map((v, j) => <td key={j} style={{ color: toneColor(r.tone, v) }}>{w(cell(v))}</td>)}
                <td style={{ color: toneColor(r.tone, r.total) }}>{w(cell(r.total))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
